// noinspection SpellCheckingInspection

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { alpha, SwitchProps, Theme } from "@mui/material";
import { green, lightBlue } from "@mui/material/colors";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import AHBoolean from "_shared/booleans/boolean";
import { Change, diffLines } from "diff";
import React, { forwardRef } from "react";
import FieldUndoButton from "work/proposals/_views/_buttons/field-undo-button";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

function getSwitchColor(params: {
  theme: Theme;
  resolved: boolean;
  canBeUndone: boolean;
  added: boolean;
  removed: boolean;
}) {
  const { theme, resolved, canBeUndone, added, removed } = params;
  if (resolved) {
    if (canBeUndone) return lightBlue[700];
    else return theme.palette.primary.main;
  } else if (added) {
    return theme.palette.success.main;
  } else if (removed) {
    return theme.palette.error.main;
  } else {
    return theme.palette.action.disabled;
  }
}

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const RedlineSwitch = styled(Switch, {
  shouldForwardProp: (prop) =>
    !["resolved", "canBeUndone", "added", "removed"].includes(prop.toString()),
})<{
  resolved: boolean;
  canBeUndone: boolean;
  added: boolean;
  removed: boolean;
}>(({ theme, resolved, canBeUndone, added, removed }) => ({
  "& .Mui-disabled.Mui-checked": {
    opacity: 0.4,
  },
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: getSwitchColor({
      theme,
      resolved,
      canBeUndone,
      added,
      removed,
    }),
    "&:hover": {
      backgroundColor: resolved
        ? alpha(
            getSwitchColor({
              theme,
              resolved,
              canBeUndone,
              added,
              removed,
            }),
            theme.palette.action.hoverOpacity
          )
        : "initial",
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: getSwitchColor({
      theme,
      resolved,
      canBeUndone,
      added,
      removed,
    }),
  },
}));
const LabelContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));
const DiffContainer = styled("div")<{ resolved?: boolean; disabled?: boolean }>(
  ({ theme, resolved, disabled }) => ({
    alignItems: "flex-start",
    cursor: resolved && !disabled ? "pointer" : "default",
    display: "flex",
    flexDirection: "column",
    marginRight: theme.spacing(1),
  })
);
const DiffResult = styled("span")<{
  change?: Change;
  resolved?: boolean;
}>(({ change, resolved, theme }) => ({
  backgroundColor: (function () {
    if (resolved) return "unset";
    if (change?.added) return green[300];
  })(),
  color: (function () {
    if (change?.added && resolved) return lightBlue[700];
    return theme.palette.text.primary;
  })(),
  textDecoration: (function () {
    if (change?.removed) return "line-through";
  })(),
  textDecorationColor: (function () {
    if (change?.removed && resolved) return lightBlue[700];
    if (change?.removed) return theme.palette.error.main;
    return "initial";
  })(),
  textDecorationThickness: '2px',
  '&:hover': {
    textDecorationColor: (function() { 
      let color = "initial";
      if (change?.removed && resolved) color = lightBlue[700];
      if (change?.removed && !resolved) color = theme.palette.error.main;
      return `oklch(from ${color} l c h / 0.25)`
    }())
  },
}));

export interface RedlineSwitchProps extends SwitchProps {
  disabled?: boolean;
  promptTitleText: string | undefined;
  promptMessageText: string | undefined;
  okButtonText: string | undefined;
  unsetLabel: string;
  setLabel: string;
  boolRedline: FieldRedline<AHBoolean>;
  disableUndo?: boolean;
  onWaiveFieldFocus?: () => void;
  onBoolRedlineChange?: (
    newRedline: FieldRedline<AHBoolean>,
    traversalFieldOverride?: ProposalField | null
  ) => void;
}

const SwitchRedline = forwardRef<HTMLInputElement, RedlineSwitchProps>(
  function SwitchRedline(
    props: Readonly<RedlineSwitchProps>,
    ref: React.ForwardedRef<HTMLInputElement>
  ) {
    const {
      disabled,
      promptTitleText,
      promptMessageText,
      okButtonText,
      unsetLabel,
      setLabel,
      boolRedline,
      disableUndo,
      onBoolRedlineChange,
      onWaiveFieldFocus,
      ...otherProps
    } = props;

    const [currentValue, setCurrentValue] = React.useState<boolean>(
      boolRedline?.currentEntry?.value ??
        boolRedline?.revisedEntry?.value ??
        false
    );
    const confirm = useConfirmDialog();

    React.useEffect(() => {
      setCurrentValue(
        boolRedline?.currentEntry?.value ??
          boolRedline?.revisedEntry?.value ??
          false
      );
    }, [boolRedline?.currentEntry, boolRedline?.revisedEntry]);

    function handleAcceptChange(event: React.MouseEvent<HTMLButtonElement>) {
      event.stopPropagation();
      event.preventDefault();
      onBoolRedlineChange?.(boolRedline?.accept());
    }

    function handleRejectChange(event: React.MouseEvent<HTMLButtonElement>) {
      event.stopPropagation();
      event.preventDefault();
      onBoolRedlineChange?.(boolRedline?.reject());
    }

    function handleUndoChange(event: React.MouseEvent<HTMLButtonElement>) {
      event.stopPropagation();
      event.preventDefault();
      onBoolRedlineChange?.(boolRedline.undo(), boolRedline.field);
    }

    function renderAcceptRejectButtons() {
      if (boolRedline.isResolved || disabled) return null;
      return (
        <>
          <Tooltip title="Accept Change">
            <span>
              <ActionButton onClick={handleAcceptChange}>
                <CheckIcon color="success" />
              </ActionButton>
            </span>
          </Tooltip>
          <Tooltip title="Reject Change">
            <span>
              <ActionButton onClick={handleRejectChange}>
                <CloseIcon color="error" />
              </ActionButton>
            </span>
          </Tooltip>
        </>
      );
    }

    function renderUndoButton() {
      if (!boolRedline.canBeUndone || disabled || disableUndo) return null;
      return <FieldUndoButton onUndo={handleUndoChange} />;
    }

    function renderActionButtons() {
      if (disabled) return null;
      return (
        <span>
          {renderAcceptRejectButtons()}
          {renderUndoButton()}
        </span>
      );
    }

    function getDiffedLabel() {
      let diff: Change[];

      if (boolRedline.isResolved) {
        diff = diffLines(
          boolRedline.revisedEntry?.value ? setLabel : unsetLabel,
          boolRedline.currentEntry?.value ? setLabel : unsetLabel
        );
      } else {
        diff = diffLines(
          boolRedline.originalEntry?.value ? setLabel : unsetLabel,
          boolRedline.revisedEntry?.value ? setLabel : unsetLabel
        );
      }

      return (
        <DiffContainer
          resolved={boolRedline.isResolved}
          disabled={disabled ?? false}
        >
          {diff.map((change: Change) => {
            return (
              <DiffResult
                key={JSON.stringify(change)}
                change={change}
                resolved={boolRedline.isResolved}
              >
                {change.value}
              </DiffResult>
            );
          })}
        </DiffContainer>
      );
    }

    async function handleOnChange(params: {
      _event: React.SyntheticEvent,
      checked: boolean
    }) {
      const { checked } = params;
      if (checked && (promptTitleText || promptMessageText || okButtonText)) {
        const response = await confirm({
          title: promptTitleText,
          message: promptMessageText,
          okButtonText: okButtonText,
        });

        if (response === ConfirmResponse.Cancel) return;
        onBoolRedlineChange?.(
          boolRedline.edit(new AHBoolean(true)),
          boolRedline.field
        );
      } else if (checked) {
        onBoolRedlineChange?.(
          boolRedline.edit(new AHBoolean(true)),
          boolRedline.field
        );
      } else {
        onBoolRedlineChange?.(
          boolRedline.edit(new AHBoolean(false)),
          boolRedline.field
        );
      }
    }

    return (
      <FormControlLabel
        disabled={disabled}
        checked={currentValue}
        control={
          <RedlineSwitch
            {...otherProps}
            disabled={disabled ?? false}
            disableRipple={disabled || !boolRedline.isResolved}
            resolved={boolRedline.isResolved}
            added={boolRedline.isAdded}
            removed={boolRedline.isRemoved}
            canBeUndone={boolRedline.canBeUndone ?? false}
            onFocus={onWaiveFieldFocus}
            slotProps={{
              input: {
                ref: ref,
              },
            }}
          />
        }
        label={
          <LabelContainer>
            {getDiffedLabel()}
            {renderActionButtons()}
          </LabelContainer>
        }
        onChange={(event) => handleOnChange({ _event: event, checked: !currentValue })}
      />
    );
  }
);

export default SwitchRedline;
